import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  Card,
  CardContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import CustomDataGridPro from "../component/CustomDataGridPro";
import customerStore from "../stores/CustomerStore";

const CustomersPage = observer(() => {
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    organizationId: "",
    locationId: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    customerStore.fetchCustomers();
    customerStore.fetchOrganizations();
    customerStore.fetchLocations();
  }, []);

  const handleSubmitCustomer = async () => {
    if (isEditMode && editingCustomerId) {
      await customerStore.updateCustomer(editingCustomerId, newCustomer);
    } else {
      await customerStore.createCustomer(newCustomer);
    }
    setNewCustomer({ name: "", organizationId: "", locationId: "" });
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingCustomerId(null);
  };

  const handleEditCustomer = (customer) => {
    setNewCustomer({
      name: customer.name,
      organizationId: customer.organization_id,
      locationId: customer.location_id,
    });
    setEditingCustomerId(customer.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 100,
      },
      {
        field: "name",
        headerName: "Customer Name",
        flex: 1,
      },
      {
        field: "organization",
        headerName: "Organization",
        flex: 1,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const customer = params.row.raw;
          return (
            <Box>
              <IconButton
                color="primary"
                onClick={() => handleEditCustomer(customer)}
                sx={{
                  color: "#22C55E",
                  mr: 1,
                  "&:hover": { backgroundColor: "#BBF7D0" },
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => customerStore.deleteCustomer(customer.id)}
                sx={{
                  color: "#6B7280",
                  "&:hover": { backgroundColor: "#E5E7EB" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    []
  );

  const rows = useMemo(() => {
    return customerStore.customers.map((customer) => {
      const orgName =
        customerStore.organizations.find(
          (org) => org.id === customer.organization_id
        )?.name || "N/A";

      const locName =
        customerStore.locations.find((loc) => loc.id === customer.location_id)
          ?.name || "N/A";

      return {
        id: customer.id,
        name: customer.name,
        organization: orgName,
        location: locName,
        raw: customer,
      };
    });
  }, [
    customerStore.customers,
    customerStore.organizations,
    customerStore.locations,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <PeopleIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Customers Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your customer data from this dashboard
          </Typography>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
          onClick={() => {
            setIsModalOpen(true);
            setIsEditMode(false);
            setNewCustomer({
              name: "",
              organizationId: "",
              locationId: "",
            });
            setEditingCustomerId(null);
          }}
          startIcon={<AddIcon />}
        >
          Add Customer
        </Button>
      </Box>

      {/* Table Section */}
      {/* <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#44475A" : "#F9FAFB",
                borderBottom: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark" ? "#6272A4" : "#E5E7EB"
                  }`,
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customerStore.customers.map((customer) => {
              const orgName =
                customerStore.organizations.find(
                  (org) => org.id === customer.organization_id
                )?.name || "N/A";
              const locName =
                customerStore.locations.find(
                  (loc) => loc.id === customer.location_id
                )?.name || "N/A";

              return (
                <TableRow key={customer.id}>
                  <TableCell>{customer.id}</TableCell>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{orgName}</TableCell>
                  <TableCell>{locName}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditCustomer(customer)}
                      sx={{
                        color: "#22C55E",
                        mr: 1,
                        "&:hover": {
                          backgroundColor: "#BBF7D0",
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => customerStore.deleteCustomer(customer.id)}
                      sx={{
                        color: "#6B7280",
                        "&:hover": {
                          backgroundColor: "#E5E7EB",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper> */}

      <CustomDataGridPro
        rows={rows}
        columns={columns}
        isLoading={customerStore.loading}
        isSkeletonOnly={customerStore.customers.length === 0}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={rows.length}
      />

      {/* Modal Dialog */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>
          {isEditMode ? "Edit Customer" : "Add New Customer"}
        </DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="Customer Name"
            fullWidth
            margin="normal"
            value={newCustomer.name}
            onChange={handleChange}
          />
          <Select
            name="organizationId"
            fullWidth
            value={newCustomer.organizationId}
            onChange={handleChange}
            displayEmpty
            sx={{ mt: 2 }}
          >
            <MenuItem value="" disabled>
              Select an Organization
            </MenuItem>
            {customerStore.organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            name="locationId"
            fullWidth
            value={newCustomer.locationId}
            onChange={handleChange}
            displayEmpty
            sx={{ mt: 2 }}
          >
            <MenuItem value="" disabled>
              Select a Location
            </MenuItem>
            {customerStore.locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmitCustomer} variant="contained">
            {isEditMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default CustomersPage;
